import React, { useRef, useState } from 'react';
import { Box, Text } from 'grommet';
import useSWR from 'swr';
import { concentration, periodDateRange, transactionsForPeriod } from '../../utils/chart-data';
import { nonRedirectFetcher } from '../../utils/errors';
import { formatCurrencySI } from '../../utils/numbers';
import TabToggle from '../TabToggle';
import ChartRow from './ChartRow';
import { ChartRowsSkeleton } from './ChartSkeleton';
import ChartCard from './ChartCard';

const LENSES = [{ name: 'Sales', value: 'sales' }, { name: 'Cash', value: 'cash' }];

// Optional: until the report has loaded once the dashboard leaves the card out.
const TopCustomers = ({ months, currency, hasActivity }) => {
    const [basis, setBasis] = useState('sales');
    const range = periodDateRange(months);
    const { data, error, isLoading } = useSWR(
        range ? `/api/reports/top-customers?basis=${basis}&start_date=${range.dateFrom}&end_date=${range.dateTo}&limit=3` : null,
        nonRedirectFetcher,
        { revalidateOnFocus: false, keepPreviousData: true, shouldRetryOnError: false },
    );
    const hasLoaded = useRef(false);
    if (data) hasLoaded.current = true;

    // Only the first load: after that the last list stays up, and so does the toggle just clicked
    if (isLoading && !data) return <ChartCard title="Top customers"><ChartRowsSkeleton noHeading /></ChartCard>;
    if (!hasLoaded.current) return null;
    // keepPreviousData holds on to the last list through a failure too, so `data` can't say this
    const failed = Boolean(error) || !data;
    const customers = failed ? [] : data.data || [];
    if (!failed && customers.length === 0 && !hasActivity) return null;
    // Until the new list arrives, what's on screen is still the previous basis
    const shown = failed ? basis : data.basis || basis;

    return (
        <ChartCard
            title="Top customers"
            action={(
                <Box flex={false}>
                    <TabToggle
                        name="top-customers-basis"
                        options={LENSES}
                        value={basis}
                        onChange={(event) => setBasis(event.target.value)}
                        labelProps={{ size: 'small' }}
                    />
                </Box>
            )}
        >
            {customers.length > 0 && <Text weight={500}>{concentration(customers, data.customer_count, shown)}</Text>}
            {customers.length === 0 && (
                <Text color="text-weak">
                    {failed ? 'This list could not load.' : `No ${shown === 'cash' ? 'payments' : 'sales'} in this period.`}
                </Text>
            )}
            {customers.length > 0 && (
                <Box>
                    {customers.map((customer) => (
                        <ChartRow
                            key={customer.uuid}
                            name={customer.name}
                            value={formatCurrencySI(customer.amount, currency)}
                            detail={`${Math.round(customer.share * 100)}%`}
                            href={shown === 'sales' ? `/customers/${customer.uuid}` : `${transactionsForPeriod(months)}&customer_filter=customer:${customer.id}`}
                            event={{ section: 'top_customers', element: `${shown}_row` }}
                        />
                    ))}
                </Box>
            )}
        </ChartCard>
    );
};

export default TopCustomers;
